import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { LayoutDashboard, LogOut } from 'lucide-react';

const TermsOfService = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const AuthNavButton = ({ isMobile = false }) => {
    if (isAuthenticated) {
      return (
        <div className="flex items-center gap-4">
          <Link
            to="/painel"
            className={`flex items-center gap-2 font-medium transition-colors ${
              isMobile 
                ? 'bg-[#A56096] text-white px-4 py-2 rounded-full text-sm' 
                : 'text-[#3A5859] hover:text-[#A56096]'
            }`}
          >
            <LayoutDashboard className="h-4 w-4" />
            {`Painel`}
          </Link>
          <button
            onClick={handleLogout}
            title="Sair"
            className={`flex items-center justify-center p-2 rounded-full transition-colors ${
              isMobile
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-red-100 text-red-600 hover:bg-red-200'
            }`}
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      );
    }

    return (
      <Link 
        to="/login" 
        className={`bg-[#A56096] text-white rounded-full hover:bg-[#945586] transition-colors font-medium ${
          isMobile ? 'px-4 py-2 text-sm' : 'px-6 py-2'
        }`}
      >
        Entrar
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#F3F6F1]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#F3F6F1]/95 backdrop-blur-md border-b border-[#E5E9E2] z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/">
                <img 
                  src="/assets/logo.png" 
                  alt="Constela Brasil" 
                  className="h-10 w-auto"
                />
              </Link>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-[#3A5859] hover:text-[#A56096] font-medium transition-colors">
                Home
              </Link>
              <Link to="/planos" className="text-[#3A5859] hover:text-[#A56096] font-medium transition-colors">
                Planos
              </Link>
              <AuthNavButton />
            </nav>

            <div className="md:hidden">
              <AuthNavButton isMobile={true} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#3A5859] mb-4">
              Termos de Serviço
            </h1>
            <p className="text-xl text-[#496566] max-w-3xl mx-auto">
              Regras e responsabilidades para a utilização da plataforma
            </p>
            <p className="text-sm text-[#496566] mt-4">
              Última atualização: Janeiro de 2025
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              
              {/* Aceitação dos Termos */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">1. Aceitação dos Termos</h2>
                <p className="text-[#496566] mb-4">
                  Ao aceder ou utilizar os serviços da <strong>Constela Brasil</strong>, você concorda em ficar vinculado 
                  a estes Termos de Serviço e à nossa <Link to="/politica-de-privacidade" className="text-[#A56096] hover:underline">Política de Privacidade</Link>. 
                  Se não concorda com qualquer parte destes termos, não deve utilizar os nossos serviços.
                </p>
                <p className="text-[#496566]">
                  Estes termos constituem um acordo legal entre você e a Constela Brasil. 
                  Recomendamos que leia atentamente todos os termos antes de utilizar a plataforma.
                </p>
              </section>

              {/* Descrição do Serviço */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">2. Descrição do Serviço</h2>
                <p className="text-[#496566] mb-4">
                  A <strong>Constela Brasil</strong> é uma plataforma online que oferece serviços de Constelação Familiar 
                  em grupo através de tecnologia virtual, proporcionando:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Sessões de Constelação Familiar conduzidas por consteladores homologados</li>
                  <li>Participação como representante em constelações de outros utilizadores</li>
                  <li>Ambiente virtual interativo com avatares e cenário digital</li>
                  <li>Videoconferência para comunicação durante as sessões</li>
                  <li>Sistema de agendamento e gestão de sessões</li>
                  <li>Planos de assinatura e pagamentos para diferentes modalidades de participação</li>
                </ul>
                <p className="text-[#496566]">
                  Os nossos serviços destinam-se exclusivamente a maiores de 18 anos ou menores acompanhados por responsáveis legais.
                </p>
              </section>

              {/* Contas de Utilizador */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">3. Contas de Utilizador</h2>
                
                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">3.1 Criação de Conta</h3>
                <p className="text-[#496566] mb-4">
                  Para utilizar os nossos serviços, deve criar uma conta fornecendo informações precisas, 
                  atualizadas e completas. É sua responsabilidade:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Manter a confidencialidade das suas credenciais de acesso</li>
                  <li>Notificar-nos imediatamente sobre qualquer uso não autorizado da sua conta</li>
                  <li>Atualizar as suas informações pessoais quando necessário</li>
                  <li>Garantir que tem idade legal para utilizar os serviços</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">3.2 Tipos de Conta</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>Participante:</strong> Pode participar como representante ou fazer a própria constelação</li>
                  <li><strong>Constelador:</strong> Profissional homologado que conduz as sessões de constelação</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">3.3 Verificação de Consteladores</h3>
                <p className="text-[#496566]">
                  Os consteladores devem fornecer comprovação de formação e certificação adequada. 
                  Reservamos o direito de verificar credenciais e negar acesso a profissionais não qualificados.
                </p>
              </section>

              {/* Regras de Conduta */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">4. Regras de Conduta</h2>
                
                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">4.1 Comportamento Durante as Sessões</h3>
                <p className="text-[#496566] mb-4">Durante a participação em sessões de constelação, você compromete-se a:</p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Manter um ambiente respeitoso e acolhedor para todos os participantes</li>
                  <li>Respeitar a confidencialidade das informações compartilhadas durante as sessões</li>
                  <li>Seguir as orientações do constelador responsável</li>
                  <li>Participar de forma ética e responsável</li>
                  <li>Não gravar, fotografar ou fazer capturas de tela das sessões sem autorização expressa</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">4.2 Condutas Proibidas</h3>
                <p className="text-[#496566] mb-4">São estritamente proibidas as seguintes condutas:</p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Assédio, discriminação ou comportamento ofensivo</li>
                  <li>COmpartilhamento de informações pessoais de outros participantes</li>
                  <li>Uso da plataforma para fins comerciais não autorizados</li>
                  <li>Tentativa de acesso não autorizado a contas de outros usuários</li>
                  <li>Interferência no funcionamento normal das sessões</li>
                  <li>Fornecimento de conselhos médicos ou psicológicos não qualificados</li>
                  <li>Uso de linguagem inadequada, ofensiva ou discriminatória</li>
                </ul>
              </section>

              {/* Planos e Pagamentos */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">5. Planos e Pagamentos</h2>
                
                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">5.1 Tipos de Planos</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>Representante Regular:</strong> Pagamento por vivência individual (R$ 25,00)</li>
                  <li><strong>Clube da Constelação:</strong> Plano mensal com acesso ilimitado às sessões do clube (R$ 29,90/mês)</li>
                  <li><strong>Sua Constelação:</strong> Sessão individual completa (R$ 530,00)</li>
                  <li><strong>Planos para Consteladores:</strong> Avulso, Premium e Master com diferentes funcionalidades</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">5.2 Processamento de Pagamentos</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Os pagamentos são processados através da plataforma de pagamentos do Mercado Pago.</li>
                  <li>Todos os preços estão expressos em Reais (BRL) e incluem impostos aplicáveis</li>
                  <li>Os pagamentos são cobrados antecipadamente</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">5.3 Reembolsos e Cancelamentos</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>Direito de Arrependimento:</strong> 7 dias para cancelar planos não utilizados</li>
                  <li><strong>Sessões Canceladas:</strong> Reembolso integral se canceladas com 24h de antecedência</li>
                  <li><strong>Problemas Técnicos:</strong> Reembolso ou nova sessão em caso de falhas na plataforma</li>
                  <li><strong>Cancelamento de Assinaturas:</strong> Pode ser feito a qualquer momento, válido para o próximo ciclo</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">5.4 Alterações de Preços</h3>
                <p className="text-[#496566]">
                  Reservamos o direito de alterar os preços dos nossos serviços mediante notificação prévia 
                  de 30 dias. As alterações não afetam pagamentos já processados ou contratos em vigor.
                </p>
              </section>

              {/* Propriedade Intelectual */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">6. Propriedade Intelectual</h2>
                
                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">6.1 Conteúdo da Plataforma</h3>
                <p className="text-[#496566] mb-4">
                  Todos os direitos de propriedade intelectual relacionados com a plataforma Constela Brasil, 
                  incluindo mas não limitado a:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Design da interface e experiência do usuário</li>
                  <li>Software e código da aplicação</li>
                  <li>Logotipos, marcas registadas e elementos visuais</li>
                  <li>Conteúdo educativo e metodologias</li>
                  <li>Tecnologia de avatares e ambiente virtual</li>
                </ul>
                <p className="text-[#496566] mb-6">
                  São de propriedade exclusiva da Constela Brasil ou dos seus licenciadores.
                </p>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">6.2 Licença de Uso</h3>
                <p className="text-[#496566] mb-4">
                  Concedemos-lhe uma licença limitada, não exclusiva e revogável para utilizar a plataforma 
                  para os fins pretendidos. Esta licença não inclui o direito de:
                </p>
                <ul className="list-disc pl-6 text-[#496566]">
                  <li>Reproduzir, distribuir ou criar obras derivadas</li>
                  <li>Fazer engenharia reversa ou tentar aceder ao código fonte</li>
                  <li>Usar os nossos serviços para criar plataformas concorrentes</li>
                  <li>Remover avisos de direitos autorais ou outras marcações de propriedade</li>
                </ul>
              </section>

              {/* Limitação de Responsabilidade */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">7. Limitação de Responsabilidade</h2>
                
                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">7.1 Natureza dos Serviços</h3>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-6">
                  <p className="text-yellow-800 font-semibold mb-2">AVISO IMPORTANTE:</p>
                  <p className="text-yellow-700">
                    Os serviços da Constela Brasil são de natureza terapêutica complementar e NÃO substituem 
                    tratamento médico, psicológico ou psiquiátrico profissional. Em casos de emergência ou 
                    crises de saúde mental, procure ajuda médica imediata.
                  </p>
                </div>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">7.2 Limitações</h3>
                <p className="text-[#496566] mb-4">
                  Na máxima extensão permitida por lei, a Constela Brasil não será responsável por:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Danos indiretos, incidentais, especiais ou consequenciais</li>
                  <li>Perda de dados, lucros ou oportunidades de negócio</li>
                  <li>Interrupções temporárias do serviço por manutenção ou problemas técnicos</li>
                  <li>Ações ou omissões de terceiros, incluindo outros utilizadores</li>
                  <li>Resultados específicos das sessões de constelação</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">7.3 Responsabilidade dos Consteladores</h3>
                <p className="text-[#496566]">
                  Os consteladores são profissionais independentes responsáveis pela condução ética 
                  e competente das suas sessões. A Constela Brasil facilita a conexão mas não assume 
                  responsabilidade pelas práticas individuais dos profissionais.
                </p>
              </section>

              {/* Rescisão de Conta */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">8. Rescisão de Conta</h2>
                
                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">8.1 Rescisão pelo Utilizador</h3>
                <p className="text-[#496566] mb-4">
                  Pode encerrar a sua conta a qualquer momento através das configurações da conta 
                  ou contatando o nosso suporte. Após o encerramento:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>O acesso aos serviços será imediatamente suspenso</li>
                  <li>Dados pessoais serão tratados conforme a nossa Política de Privacidade</li>
                  <li>Assinaturas ativas continuarão até o final do período pago</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">8.2 Rescisão pela Constela Brasil</h3>
                <p className="text-[#496566] mb-4">
                  Reservamo-nos o direito de suspender ou encerrar contas que violem estes termos, incluindo:
                </p>
                <ul className="list-disc pl-6 text-[#496566]">
                  <li>Violação das regras de conduta</li>
                  <li>Uso fraudulento dos serviços</li>
                  <li>Não pagamento de taxas devidas</li>
                  <li>Comportamento que prejudique outros usuários</li>
                </ul>
              </section>

              {/* Lei Aplicável e Foro */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">9. Lei Aplicável e Foro</h2>
                <p className="text-[#496566] mb-4">
                  Estes Termos de Serviço são regidos pelas leis da República Federativa do Brasil, 
                  especialmente:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Código de Defesa do Consumidor (Lei nº 8.078/90)</li>
                  <li>Lei Geral de Proteção de Dados (Lei nº 13.709/18)</li>
                  <li>Marco Civil da Internet (Lei nº 12.965/14)</li>
                  <li>Código Civil Brasileiro</li>
                </ul>
                <p className="text-[#496566]">
                  Para a resolução de qualquer controvérsia decorrente destes termos, fica eleito 
                  o foro da comarca de São Paulo, São Paulo, Brasil, renunciando as partes a qualquer outro.
                </p>
              </section>

              {/* Footer da página */}
              <div className="text-center pt-8 border-t border-[#E5E9E2]">
                <p className="text-sm text-[#496566] mb-2">
                  Estes Termos de Serviço complementam a nossa{' '}
                  <Link to="/politica-de-privacidade" className="text-[#A56096] hover:underline">
                    Política de Privacidade
                  </Link>
                </p>
                <p className="text-xs text-[#496566]">
                  Ao continuar a utilizar os nossos serviços, você concorda com estes termos na sua versão mais recente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;